import { Component } from '@wonderlandengine/api';
import { Object3DTween } from './Object3DTween.js';
/**
 * A component that can play animations on an object.
 *
 * Notes:
 * - At the moment the animations are only for position, rotation and scale. Other animations
 * need to be implemented manually.
 * - The queue does not take layers into account. It will just play the next clip in the queue.
 */
export class AnimationController extends Component {
    static TypeName = 'animation-controller';
    clips = new Map();
    currentClip = [];
    layers = new Map();
    update(dt) {
        this.currentClip?.forEach((clip) => {
            if (clip && clip.isPlaying()) {
                clip.update();
            }
        });
    }
    addClip(data, name) {
        const tween = Object3DTween.FromData(this.object, data);
        tween.onComplete(this.onClipComplete);
        this.clips.set(name, tween);
    }
    play(name) {
        const clip = this.clips.get(name);
        const layer = this.layers.get(name) ?? 0;
        if (clip) {
            clip.stop(); // just to make sure it is stopped, and properties are reset.
            if (this.currentClip[layer]) {
                this.currentClip[layer]?.stop();
            }
            this.currentClip[layer] = clip.start();
        }
    }
    /**
     * Stops the current clip from playing. If no name is provided, all clips are stopped.
     * @param name The name of the clip to stop; if not provided, all clips are stopped
     */
    stop(name = undefined) {
        if (!name) {
            // stop any clip that is playing
            this.currentClip.forEach((clip, index) => {
                if (clip) {
                    clip.stop();
                    this.currentClip[index] = undefined;
                }
            });
            return;
        }
        const clip = this.clips.get(name);
        const layer = this.layers.get(name) ?? 0;
        if (clip) {
            clip.stop();
            this.currentClip[layer] = undefined;
        }
    }
    /**
     * Plays a clip after the current clip has finished playing
     * @param name Name of the clip to play
     */
    playQueued(name) {
        const clip = this.clips.get(name);
        const layer = this.layers.get(name) ?? 0;
        if (clip) {
            // is there a clip playing?
            if (this.currentClip[layer]) {
                this.currentClip[layer].chain(clip);
                // queue this clip
                // this.queue.push(name);
            }
            else {
                // no clip playing, play this one
                this.play(name);
            }
        }
    }
    onClipComplete = (tween, _) => {
        const current = this.currentClip.indexOf(tween);
        if (!~current) {
            // not sure how we got here since a clip completed that was not a current clip
            console.warn('A clip completed that was not a current clip');
            return;
        }
    };
    blend(name, targetWeight = 1.0, fadeLength = 0.3) {
        // how?
    }
    setLayer(name, layer) {
        this.layers.set(name, layer);
    }
    /**
     * Gets the length of a clip in seconds
     * @param name The name of the clip to get the length of
     * @returns The length of the clip in seconds
     */
    getLength(name) {
        // note: it might be faster to keep a list of the originald data and
        // just get the length from there
        const clip = this.clips.get(name);
        return (clip?.getDuration() ?? 0) / 1000;
    }
}
/**
 * Base class for simple animations. If there's 1 animation that needs to be played
 * on an object, inherit from this class and implement the clipName and data properties.
 *
 * @example
 * ```typescript
 * import { AnimationData, AnimationClip } from 'wonderland-animation';
  * import someAnimationData from './SomeAnimation.json';
 *
 * export class SomeAnimation extends AnimationClip {
 *    static TypeName = 'anim:SomeAnimation';
 *    static InheritProperties = true;
 *
 *    public data: AnimationData = someAnimationData;
 *    public clipName: string = 'SomeAnimation';
 * }
    ```
 */
export class AnimationClip extends Component {
    static TypeName = 'animation-clip';
    controller;
    static onRegister(engine) {
        engine.registerComponent(AnimationController);
    }
    start() {
        this.controller =
            this.object.getComponent(AnimationController) ??
                this.object.addComponent(AnimationController);
        this.controller.addClip(this.data, this.clipName);
        this.controller.play(this.clipName);
    }
}
//# sourceMappingURL=AnimationController.js.map